import type { CampaignAlteration, Task } from './types'
import { stopSubtaskTimers } from './subtaskTime'

export interface RequestedCampaignAlteration {
  subtaskId: string
  reason: string
}

function alterationTimestamp(date: Date) {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  }).format(date).replace('.', '')
}

export function createCampaignAlteration(
  task: Task,
  requestedItems: RequestedCampaignAlteration[],
  requestedBy: string,
  createdOn = new Date(),
): CampaignAlteration | undefined {
  const reasonsBySubtask = new Map(requestedItems.map(item => [item.subtaskId, item.reason.trim()]))
  const items = task.subtasks.flatMap(subtask => {
    const reason = reasonsBySubtask.get(subtask.id)
    return reason
      ? [{ subtaskId: subtask.id, subtaskTitle: subtask.title, owner: subtask.owner, reason }]
      : []
  })
  if (!items.length) return undefined

  return {
    id: crypto.randomUUID(),
    requestedBy,
    createdAt: alterationTimestamp(createdOn),
    createdOn: createdOn.toISOString(),
    items,
  }
}

export function pendingAlterationForSubtask(alterations: CampaignAlteration[], subtaskId: string) {
  for (let alterationIndex = alterations.length - 1; alterationIndex >= 0; alterationIndex -= 1) {
    const item = alterations[alterationIndex].items.find(candidate => candidate.subtaskId === subtaskId && !candidate.resolvedAt)
    if (item) return item
  }
  return undefined
}

export function applyCampaignAlteration(task: Task, alteration: CampaignAlteration): Task {
  const selectedIds = new Set(alteration.items.map(item => item.subtaskId))
  return {
    ...task,
    status: 'Em alteração',
    running: false,
    runningUser: undefined,
    activeTimeEntryId: undefined,
    alterations: [...task.alterations, alteration],
    subtasks: task.subtasks.map(subtask => selectedIds.has(subtask.id)
      ? { ...subtask, status: 'Em alteração', completedAt: undefined, analysisApprovedAt: undefined, analysisApprovedBy: undefined, ...stopSubtaskTimers(subtask) }
      : subtask),
  }
}

export function resolveSubtaskAlterations(alterations: CampaignAlteration[], subtaskId: string, resolvedAt: string) {
  return alterations.map(alteration => ({
    ...alteration,
    items: alteration.items.map(item => item.subtaskId === subtaskId && !item.resolvedAt
      ? { ...item, resolvedAt }
      : item),
  }))
}

export function campaignAlterationSummary(alterations: CampaignAlteration[]) {
  const items = alterations.flatMap(alteration => alteration.items)
  return {
    total: items.length,
    pending: items.filter(item => !item.resolvedAt).length,
    cycles: alterations.length,
  }
}
